import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { DragDropContext } from "react-beautiful-dnd";
import Page from "./Page";
import AppFooter from "../components/footer/AppFooter";
import LoadingScreen from "../components/loading/LoadingScreen";
import useWindowDimensions from "../helpers/window";
import useCompletedTasksAPIClient from "../api/tasks/hooks/useCompletedTasksAPIClient";
import TaskListContainer from "../components/tasks/TaskListContainer";
import { onDragEnd } from "../helpers/dnd";
import { dispatch } from "../redux/store";
import { setTaskTargeted as setTaskTargetedAction } from "../redux/actions/tasks";
import useTaskAPIClient from "../api/task/hooks/useTaskAPIClient";
import dayjs from "dayjs";
import useContentsHeight from "../hooks/useContentsHeight";
import { deriveContextVariables } from "../hooks/useDayMessages";
import ActivitiesContextProvider from "../context/activities/ActivitiesContextProvider";
import { RandomActivitySelector } from "../components/activities";

// Dashboard Components
import DashboardContent from "../components/dashboard/DashboardContent";
import TaskGuideDialog from "../components/dashboard/TaskGuide/TaskGuideDialog";
import TaskHelperDialog from "../components/tasks/taskCreator/TaskHelperDialog";
import useCreateTaskAction from "../hooks/tasks/useCreateTaskAction";
import TaskCreationPreferenceDialog from "../components/dialogs/TaskCreationPreferenceDialog";

/**
 * Checks localStorage once to determine if today is the user's first open.
 * Must be read before useFirstVisitOfTheDay (in TasksPage) writes to it.
 */
function checkIsFirstOpenToday() {
  const lastVisit = localStorage.getItem("lastVisitDate");
  if (!lastVisit) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(lastVisit).toDateString() !== today.toDateString();
}

const DashboardPage = () => {
  // Capture once on first render, before localStorage is updated
  const isFirstOpenToday = useRef(checkIsFirstOpenToday()).current;
  const { setTaskTargeted } = useTaskAPIClient();
  const authenticated = useSelector((state) => state.user.authenticated);
  const tasks = useSelector((state) => state.tasks.taskSet);
  const { width } = useWindowDimensions();
  const { getCompletedTasks } = useCompletedTasksAPIClient();
  const contentsHeight = useContentsHeight();

  const [completedTasks, setCompletedTasks] = useState(null);
  const [taskLists, setTaskLists] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Task Guide dialog state
  const [guideOpen, setGuideOpen] = useState(false);

  // Task Helper dialog state
  const [taskHelperOpen, setTaskHelperOpen] = useState(false);
  const [taskHelperFromGuide, setTaskHelperFromGuide] = useState(false);

  // Preference-aware task creation
  const {
    triggerCreateTask,
    showPreferenceDialog,
    setShowPreferenceDialog,
    handlePreferenceSelected,
  } = useCreateTaskAction("dashboard", () => setTaskHelperOpen(true));

  // Auto-open guide if returning from task creation with saved state
  useEffect(() => {
    const savedState = sessionStorage.getItem("taskGuideState");
    if (savedState) {
      setGuideOpen(true);
    }
  }, []);

  const isDesktop = width > 768;

  // Fetch completed tasks for stats
  useEffect(() => {
    if (authenticated) {
      getCompletedTasks().then((tasks) => {
        if (tasks) {
          setCompletedTasks(tasks);
        }
      });
    }
  }, [authenticated, getCompletedTasks]);

  // Split tasks into targets and backlog for desktop view
  useEffect(() => {
    const newTaskLists = {
      targets: {
        listId: "targets",
        name: "Targets",
        tasks: Object.values(tasks).filter(
          (task) => task.targeted && !task.completed,
        ),
      },
      backlog: {
        listId: "backlog",
        name: "Tasks",
        tasks: Object.values(tasks).filter(
          (task) => !task.targeted && !task.completed,
        ),
      },
    };
    setTaskLists(newTaskLists);
  }, [tasks]);

  // Calculate context variables for messaging
  const contextVariables = useMemo(() => {
    const activeTaskArray = Object.values(tasks);
    const completedTaskArray = completedTasks
      ? Object.values(completedTasks)
      : [];
    const taskArray = [...activeTaskArray, ...completedTaskArray];
    return deriveContextVariables(taskArray, isFirstOpenToday);
  }, [tasks, completedTasks, isFirstOpenToday]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalTasks = Object.values(tasks).filter((t) => !t.completed).length;
    const targetedTasks = Object.values(tasks).filter(
      (t) => t.targeted && !t.completed,
    ).length;

    let completedToday = 0;
    let completedThisWeek = 0;
    let completedTotal = 0;
    let completedTargetsToday = 0;

    if (completedTasks) {
      const now = dayjs();
      const startOfToday = now.startOf("day");
      const startOfWeek = now.startOf("week");

      Object.values(completedTasks).forEach((task) => {
        if (task.completed) {
          completedTotal++;
          if (typeof task.completed === "number") {
            const completedTime = dayjs(task.completed);
            if (
              completedTime.isAfter(startOfToday) ||
              completedTime.isSame(startOfToday)
            ) {
              completedToday++;
              // Count targeted tasks completed today
              if (task.targeted) {
                completedTargetsToday++;
              }
            }
            if (
              completedTime.isAfter(startOfWeek) ||
              completedTime.isSame(startOfWeek)
            ) {
              completedThisWeek++;
            }
          }
        }
      });
    }

    return {
      totalTasks,
      targetedTasks,
      completedToday,
      completedThisWeek,
      completedTotal,
      completedTargetsToday,
    };
  }, [tasks, completedTasks]);

  // Get upcoming deadlines/scheduled tasks
  // Show tasks that are: overdue, less than 30% time remaining, or less than 5 days away
  const upcomingReminders = useMemo(() => {
    const now = dayjs();
    const nowUnix = now.unix();
    const fiveDaysInHours = 5 * 24;
    const upcoming = [];

    const shouldInclude = (task, timeUnix, type) => {
      const hoursUntil = dayjs.unix(timeUnix).diff(now, "hour");

      // Always include overdue items
      if (hoursUntil < 0) return true;

      // Include if less than 5 days away (minimum threshold)
      if (hoursUntil <= fiveDaysInHours) return true;

      // Include if less than 30% of time remaining (based on created date)
      if (task.created) {
        const createdInSeconds = Math.round(task.created / 1000);
        const totalDuration = timeUnix - createdInSeconds;
        if (totalDuration > 0) {
          const timeRemaining = timeUnix - nowUnix;
          const percentRemaining = (timeRemaining / totalDuration) * 100;
          if (percentRemaining <= 30) return true;
        }
      }

      return false;
    };

    Object.values(tasks).forEach((task) => {
      if (task.completed) return;

      if (task.deadline) {
        if (shouldInclude(task, task.deadline, "deadline")) {
          const deadlineTime = dayjs.unix(task.deadline);
          const hoursUntil = deadlineTime.diff(now, "hour");
          upcoming.push({
            ...task,
            reminderType: "deadline",
            timeUntil: hoursUntil,
            reminderTime: deadlineTime,
          });
        }
      }

      if (task.schedule) {
        if (shouldInclude(task, task.schedule, "scheduled")) {
          const scheduleTime = dayjs.unix(task.schedule);
          const hoursUntil = scheduleTime.diff(now, "hour");
          upcoming.push({
            ...task,
            reminderType: "scheduled",
            timeUntil: hoursUntil,
            reminderTime: scheduleTime,
          });
        }
      }
    });

    return upcoming.sort((a, b) => a.timeUntil - b.timeUntil).slice(0, 5);
  }, [tasks]);

  // Get top 3 targeted tasks
  const topTargets = useMemo(() => {
    return Object.values(tasks)
      .filter((task) => task.targeted && !task.completed)
      .slice(0, 3);
  }, [tasks]);

  // All targeted tasks
  const allTargets = useMemo(() => {
    return Object.values(tasks).filter(
      (task) => task.targeted && !task.completed,
    );
  }, [tasks]);

  const handleSearchToggle = () => {
    if (showSearch) {
      setSearchTerm("");
    }
    setShowSearch(!showSearch);
  };

  const setListsCallback = async (lists, movementDescription) => {
    const newTaskLists = {};
    lists.forEach((list) => {
      const listId = list.listId;
      newTaskLists[listId] = list;
    });

    const { taskId, from, to } = movementDescription;
    const { listId: fromListId } = from;
    const { listId: toListId } = to;

    if (toListId === "targets" && fromListId !== "targets") {
      dispatch(setTaskTargetedAction({ taskId, targeted: true }));
      await setTaskTargeted(taskId, true);
    } else if (toListId !== "targets" && fromListId === "targets") {
      dispatch(setTaskTargetedAction({ taskId, targeted: false }));
      await setTaskTargeted(taskId, false);
    }

    setTaskLists(newTaskLists);
  };

  const userHasTasks = useMemo(() => {
    if (taskLists) {
      return (
        taskLists.targets?.tasks.length + taskLists.backlog?.tasks.length > 0
      );
    }
    return false;
  }, [taskLists]);

  if (!authenticated) {
    return <LoadingScreen />;
  }

  return (
    <Page className="dashboard-page" pageKey="dashboard">
      <ActivitiesContextProvider>
        <Box
          className="dashboard-page__container"
          sx={{ maxHeight: contentsHeight }}
        >
          {/* Main Dashboard Content */}
          <Box className="dashboard-page__main scroller--grey">
            <DashboardContent
              contextVariables={contextVariables}
              stats={stats}
              topTargets={topTargets}
              allTargets={allTargets}
              upcomingReminders={upcomingReminders}
              onGuideOpen={() => setGuideOpen(true)}
              onCreateTask={triggerCreateTask}
            />

            {/* Task Guide Dialog */}
            <TaskGuideDialog
              open={guideOpen}
              onClose={() => setGuideOpen(false)}
              onOpenTaskHelper={() => {
                setGuideOpen(false);
                setTaskHelperFromGuide(true);
                triggerCreateTask();
              }}
            />

            {/* Task Helper Dialog */}
            <TaskHelperDialog
              open={taskHelperOpen}
              onClose={() => {
                setTaskHelperOpen(false);
                setTaskHelperFromGuide(false);
              }}
              skipWelcome={taskHelperFromGuide}
              onTaskCreated={() => {
                // Re-open the guide if there was saved state
                const savedState = sessionStorage.getItem("taskGuideState");
                if (savedState) {
                  setGuideOpen(true);
                }
              }}
            />

            {/* Task Creation Preference Dialog */}
            <TaskCreationPreferenceDialog
              open={showPreferenceDialog}
              onClose={() => setShowPreferenceDialog(false)}
              onSelected={handlePreferenceSelected}
            />

            {/* Random Activity Selector Dialog */}
            <RandomActivitySelector />
          </Box>

          {/* Desktop Task List Panel */}
          {isDesktop && (
            <Box className="dashboard-page__task-panel">
              <DragDropContext
                onDragEnd={onDragEnd(
                  Object.values(taskLists),
                  setListsCallback,
                )}
              >
                <div className="dashboard-page__task-list-wrapper scroller--grey">
                  {taskLists.backlog && (
                    <TaskListContainer
                      {...taskLists.backlog}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      showSearch={showSearch}
                      onSearchToggle={handleSearchToggle}
                      userHasTasks={userHasTasks}
                      compact={width <= 1600}
                      hideTitle={width <= 1350}
                      onOpenTaskHelper={() => setTaskHelperOpen(true)}
                    />
                  )}
                </div>
              </DragDropContext>
            </Box>
          )}
        </Box>
        <AppFooter page="dashboard" onOpenTaskHelper={() => setTaskHelperOpen(true)} />
      </ActivitiesContextProvider>
    </Page>
  );
};

export default DashboardPage;
